import { i18n } from '@/shared/i18n';
import type { TaxonomyFieldType, TaxonomyTextTransform } from './types';

/**
 * Display label for each field type, shared by every screen that names one.
 *
 * Getters, not values: a plain value would resolve at import and freeze the
 * copy in whatever language happened to be active. The getter shape also keeps
 * the call site (`TAXONOMY_FIELD_TYPE_LABELS[type]`) usable from pure helpers
 * as well as components, which is why this replaced the five divergent copies
 * that each screen used to keep.
 */
export const TAXONOMY_FIELD_TYPE_LABELS: Record<TaxonomyFieldType, string> = {
  get dropdown() {
    return i18n.t('knowledge:taxonomy.fieldTypes.dropdown');
  },
  get free_text() {
    return i18n.t('knowledge:taxonomy.fieldTypes.freeText');
  },
  get number() {
    return i18n.t('knowledge:taxonomy.fieldTypes.number');
  },
  get date() {
    return i18n.t('knowledge:taxonomy.fieldTypes.date');
  },
  get computed() {
    return i18n.t('knowledge:taxonomy.fieldTypes.variable');
  },
  get linked() {
    return i18n.t('knowledge:taxonomy.fieldTypes.linkedSchema');
  },
};

/**
 * Types a user may pick from a Type dropdown.
 *
 * Variable and Linked schema are configured from their column's ⋮ menu, which
 * is the only place that knows the target to point at, so they are never
 * offered here.
 */
export const TAXONOMY_SELECTABLE_FIELD_TYPES = (
  Object.keys(TAXONOMY_FIELD_TYPE_LABELS) as TaxonomyFieldType[]
).filter((t) => t !== 'computed' && t !== 'linked');

/**
 * Whether a field can draw its vocabulary from a shared list.
 *
 * Only a dropdown can. Every consumer of `listRef` already assumes it — the
 * grid makes a bound column read-only and renders the LIST's values in its
 * cells, and the generator looks the submitted value up in the list's map and
 * emits its code. So a bound free-text column is not a free-text column: the
 * user's own typing is unreachable, and a typed word that happens to match a
 * list entry is silently replaced by that entry's code inside a permanent
 * tracking code.
 *
 * Exported as a predicate rather than repeated as `type === 'dropdown'` because
 * four separate writers have to agree about it, and they did not: the binding
 * survived a type change, the grid offered it on any column, the org-template
 * copy carried it across, and the standard-list seeder matched fields by NAME.
 */
export const canBindList = (type: TaxonomyFieldType) => type === 'dropdown';

/**
 * Text transforms applied to a field's output, in the order they are offered.
 *
 * Same getter reasoning as the type labels, and shared for the same reason:
 * the Edit-field and field-template dialogs each kept their own copy.
 */
export const TAXONOMY_TEXT_TRANSFORM_OPTIONS: {
  value: TaxonomyTextTransform;
  label: string;
}[] = [
  {
    value: 'none',
    get label() {
      return i18n.t('knowledge:taxonomy.transformOptions.none');
    },
  },
  {
    value: 'upper',
    get label() {
      return i18n.t('knowledge:taxonomy.transformOptions.uppercase');
    },
  },
  {
    value: 'lower',
    get label() {
      return i18n.t('knowledge:taxonomy.transformOptions.lowercase');
    },
  },
  {
    value: 'trim',
    get label() {
      return i18n.t('knowledge:taxonomy.transformOptions.trimWhitespace');
    },
  },
];
